import type React from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Πάτημα ή παρατεταμένο πάτημα σε ένα στοιχείο (π.χ. γραμμή του καλαθιού τραπεζιού, 6/10/2026):
 *
 *  - πάτημα (κλικ, Enter, Space)                       → onTap
 *  - κράτημα `delay` ms (αφή ή ποντίκι) ή δεξί κλικ    → onLongPress (+ μικρή δόνηση όπου υπάρχει)
 *  - κίνηση πάνω από `moveTolerance` px (ο ταμίας κυλά τη λίστα), pointercancel ή έξοδος από το
 *    στοιχείο → ούτε πάτημα ούτε παρατεταμένο
 *  - κράτημα ως το όριο ΔΕΝ βγάζει και πάτημα στο άφημα, ούτε δεύτερο παρατεταμένο από το
 *    contextmenu που στέλνουν Windows (αφή) / Android για το ίδιο κράτημα
 *  - χωρίς onLongPress (π.χ. χωρίς δικαίωμα) το κράτημα και το δεξί κλικ δεν κάνουν τίποτα·
 *    το πάτημα δουλεύει κανονικά
 *  - γεγονότα από εσωτερικά κουμπιά/πεδία (−/+, ✕ …) αγνοούνται: έχουν τη δική τους δουλειά
 *
 * Το στοιχείο θέλει και `select-none` + `-webkit-touch-callout: none`, ώστε το κράτημα να μη
 * μαρκάρει κείμενο ούτε να ανοίγει το μενού του iOS.
 */

export const LONG_PRESS_DELAY_MS = 500;
export const LONG_PRESS_MOVE_TOLERANCE_PX = 10;
const LONG_PRESS_VIBRATE_MS = 15;

export interface UseLongPressOptions {
  /** Σύντομο πάτημα: κλικ / άγγιγμα, Enter ή Space. */
  onTap?: () => void;
  /** Κράτημα `delay` ms ή δεξί κλικ. Χωρίς αυτό το κράτημα δεν κάνει τίποτα. */
  onLongPress?: () => void;
  /** Πόσο κράτημα κάνει παρατεταμένο πάτημα (ms). */
  delay?: number;
  /** Πόση κίνηση (px) ακυρώνει το πάτημα — από εκεί και πέρα είναι κύλιση. */
  moveTolerance?: number;
  /** Δόνηση (ms) όταν «πιάνει» το παρατεταμένο· 0 = καμία. */
  vibrateMs?: number;
}

export interface LongPressHandlers {
  onPointerDown: React.PointerEventHandler<HTMLElement>;
  onPointerMove: React.PointerEventHandler<HTMLElement>;
  onPointerUp: React.PointerEventHandler<HTMLElement>;
  onPointerCancel: React.PointerEventHandler<HTMLElement>;
  onPointerLeave: React.PointerEventHandler<HTMLElement>;
  onContextMenu: React.MouseEventHandler<HTMLElement>;
  onClick: React.MouseEventHandler<HTMLElement>;
  onKeyDown: React.KeyboardEventHandler<HTMLElement>;
}

interface Press {
  pointerId: number;
  x: number;
  y: number;
  /** Κρατήθηκε ως το όριο (ή ήρθε contextmenu): δεν είναι πια πάτημα. */
  held: boolean;
}

const NESTED_CONTROL = [
  'button', 'a[href]', 'input', 'select', 'textarea', 'label', 'summary',
  '[role="button"]', '[role="checkbox"]', '[role="switch"]', '[contenteditable="true"]',
].join(', ');

/** Το γεγονός ξεκίνησε μέσα σε εσωτερικό κουμπί/πεδίο — όχι στο ίδιο το στοιχείο. */
function fromNestedControl(e: React.SyntheticEvent<HTMLElement>): boolean {
  const host = e.currentTarget;
  const target = e.target as Element | null;
  if (!target || target === host || typeof target.closest !== 'function') return false;
  const control = target.closest(NESTED_CONTROL);
  return control !== null && control !== host && host.contains(control);
}

export function useLongPress(options: UseLongPressOptions): { handlers: LongPressHandlers; pressing: boolean } {
  // Οι handlers διαβάζουν πάντα τα τελευταία callbacks (π.χ. η κατάσταση επιλογής άλλαξε στο μεταξύ).
  const optionsRef = useRef(options);
  useLayoutEffect(() => {
    optionsRef.current = options;
  });

  const pressRef = useRef<Press | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Το click που ακολουθεί κράτημα που ακυρώθηκε ή έφτασε το όριο δεν είναι πάτημα.
  const swallowClickRef = useRef(false);
  // Το contextmenu που ακολουθεί κράτημα ως το όριο (Windows αφή, Android) δεν είναι δεύτερο παρατεταμένο.
  const swallowMenuRef = useRef(false);
  const [pressing, setPressing] = useState(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  useEffect(() => clearTimer, [clearTimer]);

  const endPress = (swallowClick: boolean) => {
    clearTimer();
    pressRef.current = null;
    swallowClickRef.current = swallowClick;
    setPressing(false);
  };

  const fireLongPress = () => {
    const { onLongPress, vibrateMs = LONG_PRESS_VIBRATE_MS } = optionsRef.current;
    if (!onLongPress) return;
    if (vibrateMs > 0) {
      try {
        navigator.vibrate?.(vibrateMs);
      } catch {
        // χωρίς δόνηση — αδιάφορο
      }
    }
    onLongPress();
  };

  /** Το ενεργό κράτημα, αν το γεγονός ανήκει σε αυτό. */
  const activePress = (e: React.PointerEvent<HTMLElement>): Press | null => {
    const press = pressRef.current;
    return press && press.pointerId === e.pointerId ? press : null;
  };

  const handlers: LongPressHandlers = {
    onPointerDown: e => {
      if (fromNestedControl(e) || e.isPrimary === false) return;
      // Νέο πάτημα: ό,τι περίμενε από το προηγούμενο ξεχνιέται.
      clearTimer();
      swallowClickRef.current = false;
      swallowMenuRef.current = false;
      if (e.button !== 0) {
        // δεξί / μεσαίο κουμπί — το δεξί έρχεται ως contextmenu
        pressRef.current = null;
        setPressing(false);
        return;
      }
      pressRef.current = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, held: false };
      setPressing(true);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const press = pressRef.current;
        if (!press || press.held) return;
        press.held = true;
        swallowMenuRef.current = true;
        setPressing(false);
        fireLongPress();
      }, optionsRef.current.delay ?? LONG_PRESS_DELAY_MS);
    },
    onPointerMove: e => {
      const press = activePress(e);
      if (!press || press.held) return;
      const tolerance = optionsRef.current.moveTolerance ?? LONG_PRESS_MOVE_TOLERANCE_PX;
      if (Math.hypot(e.clientX - press.x, e.clientY - press.y) > tolerance) endPress(true);
    },
    onPointerUp: e => {
      const press = activePress(e);
      if (press) endPress(press.held);
    },
    onPointerCancel: e => {
      if (activePress(e)) endPress(true);
    },
    // Στην αφή το pointerleave έρχεται ΜΕΤΑ το pointerup (το κράτημα έχει ήδη τελειώσει) — αγνοείται.
    onPointerLeave: e => {
      if (activePress(e)) endPress(true);
    },
    onContextMenu: e => {
      if (fromNestedControl(e)) return;
      e.preventDefault(); // όχι το μενού του browser πάνω στο στοιχείο
      if (swallowMenuRef.current) {
        swallowMenuRef.current = false;
        return;
      }
      const press = pressRef.current;
      if (press) {
        // Το σύστημα έστειλε contextmenu πριν από το χρονόμετρό μας: αυτό είναι το παρατεταμένο.
        if (press.held) return;
        press.held = true;
        clearTimer();
        setPressing(false);
      }
      fireLongPress();
    },
    onClick: e => {
      if (fromNestedControl(e)) return;
      if (swallowClickRef.current) {
        swallowClickRef.current = false;
        return;
      }
      optionsRef.current.onTap?.();
    },
    onKeyDown: e => {
      if (e.target !== e.currentTarget) return; // τα πλήκτρα των εσωτερικών κουμπιών είναι δικά τους
      swallowClickRef.current = false;
      swallowMenuRef.current = false; // π.χ. πλήκτρο μενού / Shift+F10 → contextmenu από πληκτρολόγιο
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      e.preventDefault();
      if (!e.repeat) optionsRef.current.onTap?.();
    },
  };

  return { handlers, pressing };
}

export default useLongPress;
